import { I18nProvider } from '@lingui/react'
import { RouterProvider } from '@tanstack/react-router'
import { useUser } from '@tshared/stores/user.store'
import { ConfigProvider, Layout, theme } from 'antd'
import { App as AntApp } from 'antd'
import en from 'antd/locale/en_GB'
import 'dayjs/locale/en-gb'
import 'dayjs/locale/zh-cn'
import zh from 'antd/locale/zh_CN'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { NuqsAdapter } from 'nuqs/adapters/react'
import { useEffect, useState } from 'react'

import { getUserPermissions } from '@/apis/role.api.ts'
import ErrorBoundary from '@/components/ErrorBoundary'
import {
  i18n as linguiI18n,
  initLinguiI18n,
  switchLocale as switchLinguiLocale,
} from '@/lib/lingui/i18n'
import type { LocaleName } from '@/models'
import { type RbacUser, useRbacInit } from '@/rbac'
import { AntdStaticAppProvider } from '@/tshared/web/components/AntdStaticApp.tsx'

import { router } from '../router.tsx'
import { useLocaleName } from '../stores/setting.store.ts'

dayjs.extend(utc)
dayjs.extend(localizedFormat)
dayjs.extend(duration)
dayjs.extend(timezone)

const ANTD_LOCALE_MAP: Record<LocaleName, typeof en> = {
  'en-GB': en,
  'zh-CN': zh,
  // Ant Design has no built-in cy-GB locale package. Use en-GB as fallback.
  'cy-GB': en,
}

const DAYJS_LOCALE_MAP: Record<LocaleName, string> = {
  'en-GB': 'en-gb',
  'zh-CN': 'zh-cn',
  // Day.js has no built-in cy-GB locale package. Use en-gb for consistency.
  'cy-GB': 'en-gb',
}

export default function App() {
  const themeName = 'light' as 'light' | 'dark'
  const localeName = useLocaleName()
  const user = useUser()
  const rbacUser: RbacUser | null =
    user?.id != null ? { id: user.id, accessToken: user.accessToken, roles: user.roles } : null
  useRbacInit({
    user: rbacUser,
    getUserUisApi: getUserPermissions,
  })

  const [linguiReady, setLinguiReady] = useState(false)

  useEffect(() => {
    void initLinguiI18n().then(() => setLinguiReady(true))
  }, [])

  useEffect(() => {
    dayjs.locale(DAYJS_LOCALE_MAP[localeName] ?? 'en-gb')
    if (linguiI18n.locale !== localeName) {
      void switchLinguiLocale(localeName)
    }
  }, [localeName, linguiReady])

  useEffect(() => {
    if (themeName === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [themeName])

  if (!linguiReady) return null

  return (
    <div className={themeName} id={'app'}>
      <I18nProvider i18n={linguiI18n}>
        <>
          <NuqsAdapter>
            <ConfigProvider
              tooltip={{
                unique: false,
              }}
              locale={ANTD_LOCALE_MAP[localeName] ?? en}
              theme={{
                hashed: false,
                zeroRuntime: true,
                token: {
                  motion: true,

                  colorPrimary: '#007689',

                  colorLink: '#007689',

                  colorTextBase: '#001923',
                  colorText: '#001923',
                  colorTextSecondary: '#4E5861',

                  colorBgLayout: 'var(--ant-neutral-1)',
                  colorBorder: 'var(--ant-neutral-3)',
                  colorTextPlaceholder: 'var(--ant-neutral-6)',

                  // colorFillSecondary: '#4E5861',
                  // colorFillQuaternary: '#E6E8E8',

                  fontSizeSM: 12,
                  fontSize: 14,
                  fontSizeHeading2: 18,
                  fontSizeHeading1: 30,
                  fontFamily: 'Roboto, system-ui, sans-serif',

                  motionDurationSlow: '0.18s',
                  motionDurationMid: '0.12s',
                  motionDurationFast: '0.06s',
                  motionEaseInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
                  motionEaseOut: 'cubic-bezier(0, 0, 0.2, 1)',
                },
                algorithm: themeName === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
                components: {
                  Card: {
                    bodyPadding: 16,
                  },
                  Button: {
                    primaryShadow: 'none',
                    defaultShadow: 'none',
                  },
                },
              }}
            >
              <AntApp>
                <Layout>
                  <ErrorBoundary>
                    <RouterProvider router={router} />
                  </ErrorBoundary>
                  <AntdStaticAppProvider />
                </Layout>
              </AntApp>
            </ConfigProvider>
          </NuqsAdapter>
        </>
      </I18nProvider>
    </div>
  )
}
